import React, { useState } from 'react';
import { Gift, Coins, Heart, Star, Coffee, Smile, Lightbulb, Target, TrendingUp } from 'lucide-react';

const Under200Page = ({ setCurrentPage }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const budgetCategories = [
    { id: 'thoughtful', name: 'Gjennomtenkte småting', icon: Heart, color: 'pink' },
    { id: 'practical', name: 'Praktisk hverdagsglede', icon: Coffee, color: 'blue' },
    { id: 'personal', name: 'Personlige oppmerksomheter', icon: Star, color: 'purple' },
    { id: 'creative', name: 'Kreative overraskelser', icon: Lightbulb, color: 'yellow' },
    { id: 'experiences', name: 'Små opplevelser', icon: Smile, color: 'green' }
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-success-50 via-accent-50 to-purple-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-8xl mb-6">💸</div>
            <h1 className="text-5xl font-bold text-secondary-800 mb-4">Gaver under 200 kr</h1>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto mb-8">
              Små summer, store gleder! Oppdag hvordan du kan gi meningsfulle gaver som viser at du bryr deg 
              uten å sprenge budsjettet. Det handler om tanken bak, ikke prislappen.
            </p>
            <button
              onClick={() => setCurrentPage('gift-finder')}
              className="px-8 py-4 bg-gradient-to-r from-accent-500 to-indigo-600 text-white rounded-lg hover:from-accent-600 hover:to-indigo-700 transition-all transform hover:scale-105 shadow-lg font-semibold"
            >
              <Gift className="w-5 h-5 inline mr-2" />
              Finn budsjettgaver
            </button>
          </div>
        </div>
      </section>

      {/* Philosophy of Budget Gifts */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Hvorfor små gaver kan bety mest
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="bg-gradient-to-br from-success-50 to-emerald-50 rounded-lg p-6 border border-success-100">
                <Heart className="w-12 h-12 text-success-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Tanken som teller</h3>
                <p className="text-secondary-600">
                  En gjennomtenkt gave til 150 kr kan bety mer enn en dyr gave uten personlig tilknytning. 
                  Det viser at du har brukt tid og energi på å tenke på personen.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-indigo-50 rounded-lg p-6 border border-accent-100">
                <Star className="w-12 h-12 text-accent-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Tilgjengelighet for alle</h3>
                <p className="text-secondary-600">
                  Ikke alle har råd til dyre gaver, og det er helt greit! Budsjettgaver gjør det mulig 
                  for alle å delta i gavegivning uten finansielt stress.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-primary-50 rounded-lg p-6 border border-purple-100">
                <Smile className="w-12 h-12 text-purple-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Spontane gleder</h3>
                <p className="text-secondary-600">
                  Små gaver kan gis spontant uten stor planlegging. De skaper overraskende øyeblikk 
                  av glede i hverdagen og viser spontan omsorg.
                </p>
              </div>
            </div>

            <div className="bg-warning-50 border border-warning-200 rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <Coins className="w-6 h-6 text-warning-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-warning-800 mb-2">Viktig å huske</h3>
                  <p className="text-warning-700">
                    Kvaliteten på en gave måles ikke i kroner og øre, men i hvor godt den matcher 
                    mottakerens behov, ønsker eller personlighet. En god gave under 200 kr kan være 
                    mer verdifull enn en dyr gave som ikke treffer i mål.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Budget Gift Categories */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Kategorier for budsjettgaver
            </h2>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 rounded-lg border transition-colors ${
                  selectedCategory === 'all'
                    ? 'bg-success-500 text-white border-success-500'
                    : 'bg-white text-secondary-700 border-secondary-300 hover:border-success-300'
                }`}
              >
                Alle kategorier
              </button>
              {budgetCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-lg border transition-colors flex items-center space-x-2 ${
                    selectedCategory === category.id
                      ? `bg-${category.color}-500 text-white border-${category.color}-500`
                      : 'bg-white text-secondary-700 border-secondary-300 hover:border-secondary-400'
                  }`}
                >
                  <category.icon className="w-4 h-4" />
                  <span>{category.name}</span>
                </button>
              ))}
            </div>

            {/* Category Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Thoughtful Items */}
              <div className={`bg-white rounded-lg shadow-sm p-8 border border-secondary-100 ${
                selectedCategory === 'all' || selectedCategory === 'thoughtful' ? 'block' : 'hidden'
              }`}>
                <div className="flex items-center mb-6">
                  <Heart className="w-8 h-8 text-primary-500 mr-3" />
                  <h3 className="text-2xl font-semibold text-secondary-800">Gjennomtenkte småting</h3>
                </div>
                <div className="space-y-4">
                  <div className="border-l-4 border-primary-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">☕ Hverdagskomfort (50-150 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Spesialkaffe eller te-blandinger</li>
                      <li>• Varme ullsokker eller tøfler</li>
                      <li>• Duftlys eller balsam</li>
                      <li>• Fine såper eller badekuler</li>
                    </ul>
                  </div>
                  
                  <div className="border-l-4 border-primary-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">🌸 Små luksurier (100-200 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Kvalitetschokolade eller konfekt</li>
                      <li>• Vakre notisbøker eller journaler</li>
                      <li>• Miniparfyme eller duftprøver</li>
                      <li>• Plantesukkulenter i pene potter</li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-6 bg-primary-50 p-3 rounded text-xs text-primary-700">
                  💝 Perfekt for: Kolleger, bekjente, eller når du vil vise at du tenker på noen
                </div>
              </div>

              {/* Practical Items */}
              <div className={`bg-white rounded-lg shadow-sm p-8 border border-secondary-100 ${
                selectedCategory === 'all' || selectedCategory === 'practical' ? 'block' : 'hidden'
              }`}>
                <div className="flex items-center mb-6">
                  <Coffee className="w-8 h-8 text-accent-500 mr-3" />
                  <h3 className="text-2xl font-semibold text-secondary-800">Praktisk hverdagsglede</h3>
                </div>
                <div className="space-y-4">
                  <div className="border-l-4 border-accent-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">🏠 Hjemme og kontor (75-175 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Termokrus for kaffe på farten</li>
                      <li>• Organisering og oppbevaringsbokser</li>
                      <li>• Kontorrekvisita i fin kvalitet</li>
                      <li>• Kjøkkengadgets og små hjelpemidler</li>
                    </ul>
                  </div>
                  
                  <div className="border-l-4 border-accent-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">🚗 På farten (50-150 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Portable ladebanker</li>
                      <li>• Reise-organiserere</li>
                      <li>• Praktiske nøkkelringer</li>
                      <li>• Små første-hjelp kit</li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-6 bg-accent-50 p-3 rounded text-xs text-accent-700">
                  🎯 Perfekt for: Folk som setter pris på funksjonalitet og hverdagshjelpere
                </div>
              </div>

              {/* Personal Items */}
              <div className={`bg-white rounded-lg shadow-sm p-8 border border-secondary-100 ${
                selectedCategory === 'all' || selectedCategory === 'personal' ? 'block' : 'hidden'
              }`}>
                <div className="flex items-center mb-6">
                  <Star className="w-8 h-8 text-purple-500 mr-3" />
                  <h3 className="text-2xl font-semibold text-secondary-800">Personlige oppmerksomheter</h3>
                </div>
                <div className="space-y-4">
                  <div className="border-l-4 border-purple-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">📸 Minner og nostalgi (80-180 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Små fotoalbum eller bilderammer</li>
                      <li>• Personaliserte nøkkelringer</li>
                      <li>• Små graverte gjenstander</li>
                      <li>• Hjemmelagde scrapbooks</li>
                    </ul>
                  </div>
                  
                  <div className="border-l-4 border-purple-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">✍️ Hobbyer og interesser (100-200 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Spesialitet-produkter for deres hobby</li>
                      <li>• Små samlerobjekter</li>
                      <li>• Tema-relaterte bøker eller magasiner</li>
                      <li>• DIY-sett for kreative prosjekter</li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-6 bg-purple-50 p-3 rounded text-xs text-purple-700">
                  ⭐ Perfekt for: Familie og nære venner som du kjenner godt
                </div>
              </div>

              {/* Creative Items */}
              <div className={`bg-white rounded-lg shadow-sm p-8 border border-secondary-100 ${
                selectedCategory === 'all' || selectedCategory === 'creative' ? 'block' : 'hidden'
              }`}>
                <div className="flex items-center mb-6">
                  <Lightbulb className="w-8 h-8 text-warning-500 mr-3" />
                  <h3 className="text-2xl font-semibold text-secondary-800">Kreative overraskelser</h3>
                </div>
                <div className="space-y-4">
                  <div className="border-l-4 border-warning-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">🎨 Hjemmelaget med kjærlighet (20-100 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Hjemmebakte kaker eller cookies</li>
                      <li>• Strikkede plagg eller tilbehør</li>
                      <li>• Selvlagde syltetøy eller krydder</li>
                      <li>• Håndverk og kunstprosjekter</li>
                    </ul>
                  </div>
                  
                  <div className="border-l-4 border-warning-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">🎁 Kreativ innpakning (50-150 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Flere små gaver i en "opplevelseskasse"</li>
                      <li>• Tema-baserte gavesett</li>
                      <li>• Skattejakt med små premier</li>
                      <li>• Personaliserte gavebokser</li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-6 bg-warning-50 p-3 rounded text-xs text-warning-700">
                  💡 Perfekt for: Når du vil vise kreativitet og har brukt tid på å lage noe unikt
                </div>
              </div>

              {/* Small Experiences */}
              <div className={`bg-white rounded-lg shadow-sm p-8 border border-secondary-100 ${
                selectedCategory === 'all' || selectedCategory === 'experiences' ? 'block' : 'hidden'
              }`}>
                <div className="flex items-center mb-6">
                  <Smile className="w-8 h-8 text-success-500 mr-3" />
                  <h3 className="text-2xl font-semibold text-secondary-800">Små opplevelser</h3>
                </div>
                <div className="space-y-4">
                  <div className="border-l-4 border-success-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">☕ Sosiale opplevelser (100-200 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Kaffe eller lunch sammen</li>
                      <li>• Kinobilletter eller streaming-abonnement</li>
                      <li>• Museum eller galleri-besøk</li>
                      <li>• Mini-spa opplevelse hjemme</li>
                    </ul>
                  </div>
                  
                  <div className="border-l-4 border-success-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">🎪 Aktiviteter og læring (75-175 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Online kurs eller workshop</li>
                      <li>• Lokale aktiviteter og arrangementer</li>
                      <li>• Abonnement på magasin eller podcast</li>
                      <li>• Mini-event eller festival-billetter</li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-6 bg-success-50 p-3 rounded text-xs text-success-700">
                  🌟 Perfekt for: Skape minner og dele tid sammen fremfor fysiske gjenstander
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Shopping Strategies */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Smart shopping for budsjettgaver
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-accent-50 to-indigo-50 rounded-lg p-6 border border-accent-100">
                <Target className="w-12 h-12 text-accent-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Hvor finne gode tilbud</h3>
                <ul className="text-sm text-secondary-600 space-y-2">
                  <li>• Outlet-butikker og clearance-salg</li>
                  <li>• Online rabattkoder og medlemsfordeler</li>
                  <li>• Årlige salg-sesonger (januar, juli)</li>
                  <li>• Lokale markeder og håndverksmesser</li>
                  <li>• Vintage og bruktbutikker</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-success-50 to-emerald-50 rounded-lg p-6 border border-success-100">
                <TrendingUp className="w-12 h-12 text-success-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Maksimer verdien</h3>
                <ul className="text-sm text-secondary-600 space-y-2">
                  <li>• Kjøp i bulk for flere gaver</li>
                  <li>• Kombiner flere små ting til et sett</li>
                  <li>• Velg tidløse ting fremfor trender</li>
                  <li>• Fokus på kvalitet innenfor budsjett</li>
                  <li>• Sammenlign priser på nett først</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-primary-50 rounded-lg p-6 border border-purple-100">
                <Lightbulb className="w-12 h-12 text-purple-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Kreative strategier</h3>
                <ul className="text-sm text-secondary-600 space-y-2">
                  <li>• Lag gaver selv med billige materialer</li>
                  <li>• Grupper deg med andre for større gave</li>
                  <li>• Bruk ferdigheter du har (baking, håndverk)</li>
                  <li>• Oppdag lokale produsenter</li>
                  <li>• Gjenbruk og "upcycle" eksisterende ting</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Occasions for Budget Gifts */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Anledninger hvor budsjettgaver er perfekte
            </h2>

            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-success-500">
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">🎉 Hverdagsoppmerksomheter</h3>
                <p className="text-secondary-600 mb-3">
                  Små gaver er perfekte for spontane øyeblikk når du vil vise at du tenker på noen.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <ul className="text-secondary-600 space-y-1">
                    <li>• Takk for hjelp eller tjeneste</li>
                    <li>• Oppmuntring under vanskelige tider</li>
                    <li>• Feiring av små milepæler</li>
                    <li>• Vertskap-takk etter besøk</li>
                  </ul>
                  <ul className="text-secondary-600 space-y-1">
                    <li>• Kollega som har vært snill</li>
                    <li>• Beklager-gave etter krangling</li>
                    <li>• Bare-fordi-oppmerksomheter</li>
                    <li>• Velkommen-gave til nye naboer</li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-accent-500">
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">👥 Gruppe-situasjoner</h3>
                <p className="text-secondary-600 mb-3">
                  Når mange skal gi gaver samtidig, er det smart at alle holder samme budsjett-nivå.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <ul className="text-secondary-600 space-y-1">
                    <li>• Kollega-bursdager på jobben</li>
                    <li>• Klasselærer-gaver fra alle foreldrene</li>
                    <li>• Secret Santa og jolegaver</li>
                    <li>• Avskjeds-gaver til kolleger</li>
                  </ul>
                  <ul className="text-secondary-600 space-y-1">
                    <li>• Barnebursdag med mange gjester</li>
                    <li>• Familie-sammenkomster</li>
                    <li>• Vennegruppe-gaver</li>
                    <li>• Arbeidsgruppe-anerkjennelser</li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-purple-500">
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">🎁 Mange gaver samtidig</h3>
                <p className="text-secondary-600 mb-3">
                  Noen perioder krever flere gaver på kort tid, og da er budsjettgaver livredder.
                </p>
                <div className="text-sm text-secondary-600">
                  <p><strong>Eksempler:</strong> Juletiden (mange familiemedlemmer), konfirmasjonssesong 
                  (flere invitasjoner), bryllupssesong, eller når du har tøff økonomi men vil fortsatt vise at du bryr deg.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Presentation Tips */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Gjør budsjettgaver ekstra spesielle
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-primary-50 to-rose-50 rounded-lg p-6 border border-primary-100">
                <Gift className="w-12 h-12 text-primary-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-4">Presentasjon og innpakning</h3>
                <ul className="space-y-2 text-sm text-secondary-600">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-primary-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Bruk tid på vakker innpakning - det øker opplevd verdi</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-primary-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Lag egne gavebokser eller pose med personlig preg</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-primary-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Kombiner flere små ting til et tema-sett</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-primary-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Legg ved en gjennomtenkt og personlig hilsen</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-indigo-50 rounded-lg p-6 border border-accent-100">
                <Heart className="w-12 h-12 text-accent-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-4">Timing og kontekst</h3>
                <ul className="space-y-2 text-sm text-secondary-600">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-accent-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Gi gaven på rett tidspunkt når det betyr mest</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-accent-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Forklar bakgrunnen - hvorfor akkurat denne gaven</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-accent-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Vær tilstede når gaven åpnes hvis mulig</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-accent-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Ikke unnskylden for at gaven er "liten" - vær stolt!</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-8 bg-success-50 border border-success-200 rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <Star className="w-6 h-6 text-success-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-success-800 mb-2">Hemmeligheten bak suksessrike budsjettgaver</h3>
                  <p className="text-success-700">
                    De beste budsjettgavene er de som viser at du virkelig kjenner personen. En pakke med deres 
                    favorittkjeks til 50 kr kan glede mer enn en tilfeldig ting til 500 kr. Observer hva de liker, 
                    trenger eller snakker om - så finner du alltid noe perfekt innenfor budsjett.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-success-500 to-accent-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Klar til å finne den perfekte budsjettgaven?
            </h2>
            <p className="text-xl text-success-100 mb-8">
              Bruk vår gavefinner for å oppdage meningsfulle gaver som passer ditt budsjett
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setCurrentPage('gift-finder')}
                className="px-8 py-4 bg-white text-success-600 rounded-lg hover:bg-secondary-100 transition-colors font-semibold shadow-lg"
              >
                <Gift className="w-5 h-5 inline mr-2" />
                Start gavefinneren
              </button>
              <button
                onClick={() => setCurrentPage('price')}
                className="px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white hover:text-success-600 transition-colors font-semibold"
              >
                Se andre prisklasser
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Under200Page;